import React, { useMemo } from 'react';
import {
  FormFieldType,
  TableNoRowMessage
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import { getService } from '@Client/runner.hooks/useService';

type Props = {
  fieldType: string;
  fieldValue: string;
};

const FlowHistoryFieldValue = (props: Props) => {
  const { fieldType, fieldValue } = props;

  const { formValue, noValueText } = useMemo(() => {
    const isFieldEmpty = () => {
      return (
        !fieldValue ||
        fieldValue === 'Please Choose' ||
        fieldValue === 'null' ||
        fieldValue === null ||
        //ignore empty currency field
        fieldValue === '$null'
      );
    };

    const formatCurrency = (value: string) => {
      if (!value) {
        return value;
      }
      const currencyRegex = /([^0-9.,]*)([0-9,.]+)([^0-9.,]*)/;
      const match = value.match(currencyRegex);
      if (!match || !match[2]) {
        return value;
      }
      const amount = match[2].replaceAll(',', '');
      const amountChars = parseFloat(amount).toFixed(2).split('');
      let dollarFigure = 0;
      for (let i = amountChars.length - 7; i >= 0; i--) {
        if (dollarFigure % 3 === 0) {
          amountChars[i] = amountChars[i] + ',';
        }
        dollarFigure++;
      }
      return match[1] + amountChars.join('') + match[3];
    };

    const makeLinksClickable = (value: string) => {
      switch (fieldType) {
        case 'textArea': {
          //the kendo widget already converts links to HTML links,
          // but it does not add target blank so they do not open in another window
          const linkPattern = new RegExp(/<a href="/g);
          //find them and add in target blank
          return value.replace(linkPattern, () => {
            return `<a target="_blank" href="`;
          });
        }
        case 'string': {
          //find any url and wrap in a link
          const urlPattern = /(?:https?:\/\/|www\.)[^\s<>"']*/g;
          return value.replace(urlPattern, (url) => {
            return `<a target="_blank" rel="noopener noreferrer" href="${
              url.startsWith('www.') ? `https://${url}` : url
            }">${url}</a>`;
          });
        }
        default:
          return value;
      }
    };

    if (isFieldEmpty()) {
      let defaultMessage: string;
      switch (fieldType) {
        case 'fileupload':
          defaultMessage = 'No file was uploaded.';
          break;
        case 'table':
          defaultMessage = TableNoRowMessage.TABLE_NO_ROW;
          break;
        default:
          defaultMessage = 'No information was entered.';
      }
      return { formValue: defaultMessage, noValueText: true };
    }

    let processedValue = fieldValue.trim();

    if (fieldType === FormFieldType.CURRENCY) {
      processedValue = formatCurrency(processedValue);
    }

    if (
      (processedValue.startsWith('[') && processedValue.endsWith(']')) ||
      (processedValue.startsWith('{') && processedValue.endsWith('}'))
    ) {
      if (fieldType == 'dropdown') {
        try {
          processedValue = JSON.parse(processedValue).text;
        } catch (e) {
          // Keep original value if parsing fails
        }
      }

      if (fieldType == 'lookup') {
        try {
          processedValue = JSON.parse(processedValue)
            .map((f) => f.text)
            .join(', ');
        } catch (e) {
          // Keep original value if parsing fails
        }
      }
    }

    const sanitizedValue = getService('validationService').sanitizeString(
      makeLinksClickable(processedValue)
    );

    return { formValue: sanitizedValue, noValueText: false };
  }, [fieldType, fieldValue]);

  return (
    <span
      className={`field-value ${noValueText ? 'no-value-text' : ''}`}
      dangerouslySetInnerHTML={{ __html: formValue }}
    />
  );
};

export default FlowHistoryFieldValue;
